import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Plus, 
  Mail, 
  Shield, 
  MoreHorizontal, 
  Edit, 
  Trash2, 
  CheckCircle,
  Clock,
  XCircle,
  UserPlus
} from 'lucide-react';
import { TeamMember } from '../../lib/supabase';
import Button from '../Button';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/api';

interface CreateTeamMemberForm {
  email: string;
  full_name: string;
  role: 'member' | 'manager' | 'admin';
  permissions: {
    canCreateMockups: boolean;
    canManageAssets: boolean;
    canManageClients: boolean;
    canViewAnalytics: boolean;
    mockupLimit?: number;
  };
}

const TeamMembers: React.FC = () => {
  const { user, session } = useAuth();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [formData, setFormData] = useState<CreateTeamMemberForm>({
    email: '',
    full_name: '',
    role: 'member',
    permissions: {
      canCreateMockups: true,
      canManageAssets: false,
      canManageClients: false,
      canViewAnalytics: false,
    }
  });

  // Fetch team members
  const fetchTeamMembers = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.AGENCY.TEAM_MEMBERS, {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTeamMembers(data.teamMembers);
      } else {
        console.error('Failed to fetch team members');
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamMembers();
  }, [user]);

  // Create team member
  const handleCreateMember = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch(API_ENDPOINTS.AGENCY.TEAM_MEMBERS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchTeamMembers();
        setShowCreateForm(false);
        resetForm();
      } else {
        const error = await response.json();
        console.error('Failed to create team member:', error);
      }
    } catch (error) {
      console.error('Error creating team member:', error);
    }
  };

  // Update team member
  const handleUpdateMember = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingMember) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.AGENCY.TEAM_MEMBERS}/${editingMember.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchTeamMembers();
        setEditingMember(null);
        resetForm();
      } else {
        const error = await response.json();
        console.error('Failed to update team member:', error);
      }
    } catch (error) {
      console.error('Error updating team member:', error);
    }
  };

  // Delete team member
  const handleDeleteMember = async (memberId: string) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.AGENCY.TEAM_MEMBERS}/${memberId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
        }
      });

      if (response.ok) {
        await fetchTeamMembers();
      } else {
        const error = await response.json();
        console.error('Failed to delete team member:', error);
      }
    } catch (error) {
      console.error('Error deleting team member:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      full_name: '',
      role: 'member',
      permissions: {
        canCreateMockups: true,
        canManageAssets: false,
        canManageClients: false,
        canViewAnalytics: false,
      }
    });
  };

  const startEdit = (member: TeamMember) => {
    setEditingMember(member);
    setFormData({
      email: member.email,
      full_name: member.full_name,
      role: member.role,
      permissions: member.permissions
    });
    setShowCreateForm(true);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'suspended':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case 'active':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'pending':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'suspended':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getRoleBadge = (role: string) => {
    const baseClasses = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium";
    switch (role) {
      case 'admin':
        return `${baseClasses} bg-purple-100 text-purple-800`;
      case 'manager':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'member':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-purple"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Team Members</h2>
          <p className="text-gray-600 mt-1">Manage your agency team and permissions</p>
        </div>
        <Button
          onClick={() => setShowCreateForm(true)}
          variant="primary"
          size="medium"
        >
          <Plus className="w-4 h-4 mr-2" />
          Invite Team Member
        </Button>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {editingMember ? 'Edit Team Member' : 'Invite Team Member'}
            </h3>
            <Button
              onClick={() => {
                setShowCreateForm(false);
                setEditingMember(null);
                resetForm();
              }}
              variant="tertiary"
              size="small"
            >
              Cancel
            </Button>
          </div>

          <form onSubmit={editingMember ? handleUpdateMember : handleCreateMember} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-brand-purple"
                  placeholder="team@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-brand-purple"
                  placeholder="John Doe"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-brand-purple"
              >
                <option value="member">Member</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Permissions</label>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.permissions.canCreateMockups}
                    onChange={(e) => setFormData({
                      ...formData,
                      permissions: { ...formData.permissions, canCreateMockups: e.target.checked }
                    })}
                    className="rounded border-gray-300 text-brand-purple focus:ring-brand-purple"
                  />
                  <span className="ml-2 text-sm text-gray-700">Can create mockups</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.permissions.canManageAssets}
                    onChange={(e) => setFormData({
                      ...formData,
                      permissions: { ...formData.permissions, canManageAssets: e.target.checked }
                    })}
                    className="rounded border-gray-300 text-brand-purple focus:ring-brand-purple"
                  />
                  <span className="ml-2 text-sm text-gray-700">Can manage assets</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.permissions.canManageClients}
                    onChange={(e) => setFormData({
                      ...formData,
                      permissions: { ...formData.permissions, canManageClients: e.target.checked }
                    })}
                    className="rounded border-gray-300 text-brand-purple focus:ring-brand-purple"
                  />
                  <span className="ml-2 text-sm text-gray-700">Can manage clients</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.permissions.canViewAnalytics}
                    onChange={(e) => setFormData({
                      ...formData,
                      permissions: { ...formData.permissions, canViewAnalytics: e.target.checked }
                    })}
                    className="rounded border-gray-300 text-brand-purple focus:ring-brand-purple"
                  />
                  <span className="ml-2 text-sm text-gray-700">Can view analytics</span>
                </label>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-4">
              <Button type="submit" variant="primary" size="medium">
                {editingMember ? 'Update Member' : 'Send Invitation'}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingMember(null);
                  resetForm();
                }}
                variant="tertiary"
                size="medium"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Team Members List */}
      {teamMembers.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <UserPlus className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No team members yet</h3>
          <p className="text-gray-600 mb-6">
            Start building your team by inviting members to collaborate on mockup projects.
          </p>
          <Button
            onClick={() => setShowCreateForm(true)}
            variant="primary"
            size="medium"
          >
            <Plus className="w-4 h-4 mr-2" />
            Invite Your First Team Member
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Team Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {teamMembers.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-brand-purple rounded-full flex items-center justify-center text-white text-sm font-medium">
                          {member.full_name.charAt(0).toUpperCase()}
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">{member.full_name}</div>
                          <div className="text-sm text-gray-500">{member.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getRoleBadge(member.role)}>
                        {member.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(member.status)}
                        <span className={`ml-2 ${getStatusBadge(member.status)}`}>
                          {member.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {member.joined_at 
                        ? new Date(member.joined_at).toLocaleDateString()
                        : 'Pending'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => startEdit(member)}
                          className="text-brand-purple hover:text-brand-purple-dark p-1 rounded"
                          title="Edit member"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteMember(member.id)}
                          className="text-red-600 hover:text-red-700 p-1 rounded"
                          title="Remove member"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamMembers;